import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  forwardRef,
  OnDestroy,
} from '@angular/core'
import {NgSelectComponent} from '@ng-select/ng-select'

import {Observable, Subject, BehaviorSubject} from 'rxjs'
import {
  distinctUntilChanged,
  takeUntil,
  finalize,
  debounceTime,
} from 'rxjs/operators'

import {TerminalService} from '@app/core/services/rest-api'
import {Terminal} from '@app/core/services/rest-api'
import {LoggerService} from '@app/core/services/logger.service'
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms'

@Component({
  selector: 'app-terminal-selector, terminal-selector',
  templateUrl: './terminal-selector.component.html',
  providers: [
    TerminalService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TerminalSelectorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TerminalSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  constructor(
    private terminalsvc: TerminalService,
    private logger: LoggerService,
  ) {}

  @ViewChild(NgSelectComponent, {static: true}) select: NgSelectComponent

  @Input() placeholder = ''
  @Input() domain
  @Input() extraQueryFilter

  @Output() changed = new EventEmitter<string>()

  public typeahead = new EventEmitter<string>()

  public search: string = ''
  public offset: number = 0
  public total: number = 0

  private loadingBehaviour: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null)
  public readonly $loading: Observable<boolean> =
    this.loadingBehaviour.asObservable()

  private loadedListBehaviour: BehaviorSubject<Terminal[]> =
    new BehaviorSubject<Terminal[]>(null)
  public readonly $loadedList: Observable<Terminal[]> =
    this.loadedListBehaviour.asObservable()

  ngOnInit() {
    this.typeahead
      .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.end$))
      .subscribe((search) => {
        this.load(search)
      })
  }

  private end$: Subject<void> = new Subject<void>()
  ngOnDestroy() {
    this.end$.next()
    this.end$.complete()
  }

  public load(value: string = undefined) {
    this.loadingBehaviour.next(true)

    if (value) {
      this.search = '+(serialNumber:' + value + '*)'
    } else {
      this.search = '+(serialNumber:*)'
    }

    if (this.domain) {
      this.search += ' +(domain:' + this.domain + ')'
    }

    if (this.extraQueryFilter) {
      this.search += ' ' + this.extraQueryFilter
    }

    this.offset = 0
    this.loadedListBehaviour.next(null)

    this.loadMore()
  }

  public loadMore() {
    this.loadingBehaviour.next(true)
    this.terminalsvc
      .getTerminals({
        search: this.search,
        asc: true,
        sort: 'serialNumber',
        offset: this.offset,
        contracts: false,
        results: 20,
      })
      .pipe(
        finalize(() => {
          this.loadingBehaviour.next(false)
        }),
      )
      .subscribe((res) => {
        this.offset += res.results.length
        this.total = res.count
        if (this.loadedListBehaviour.value) {
          this.loadedListBehaviour.next(
            this.loadedListBehaviour.value.concat(res.results),
          )
        } else {
          this.loadedListBehaviour.next(res.results)
        }
      })
  }

  change(terminal) {
    if (terminal) {
      this.changed.emit(terminal)
    } else {
      this.changed.emit(null)
    }
  }

  compare = (a: Terminal, b: Terminal) => a.serialNumber === b.serialNumber

  writeValue(obj: any) {
    /* Ensure the value is in the loadedList */
    if (obj) {
      this.load(obj.serialNumber)
    } else {
      this.load()
    }

    this.select.writeValue(obj)
  }

  registerOnChange(fn: any) {
    this.select.registerOnChange(fn)
  }

  registerOnTouched(fn: any) {
    this.select.registerOnTouched(fn)
  }
}
